import { Button } from "@components/ui/button";
import { apiClient } from "@lib/apiClient";
import { useLanguage } from "@lib/utils/hooks/useLanguage";
import { useRoutes } from "@lib/utils/hooks/useRoutes";
import { Language } from "@repo/i18n";
import { PathOverviewResponse } from "@repo/shared-types";
import { ArrowRight } from "lucide-react";
import { X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

import { PathPopupContentLoader } from "./PathPopupContentLoader";

async function getData(id: string, language: Language) {
  try {
    return await apiClient(language).getPathOverview(id);
  } catch {
    return null;
  }
}

export function PathPopupContent({
  id,
  name,
  region,
  clear,
}: {
  id: string;
  name: string;
  region: string;
  clear?: () => void;
}) {
  const language = useLanguage();
  const [data, setData] = useState<PathOverviewResponse | undefined>(undefined);
  const routes = useRoutes();
  const t = useTranslations("Map");

  useEffect(() => {
    async function getData() {
      const data = await apiClient(language).getPathOverview(id);
      setData(data);
    }
    getData();
  }, [id, language]);

  if (!data)
    return <PathPopupContentLoader name={name} region={region} clear={clear} />;

  return (
    <div className="items-top flex flex-col justify-start gap-4 p-4 md:p-0">
      <div className="mt-10 flex flex-row justify-between md:mt-0">
        <div className="flex flex-col">
          <p className="text-base font-bold md:text-lg">{name}</p>
          <p className="text-base md:text-xs">{region}</p>
          <span className="mt-3 text-sm md:text-base">
            {data.length} {t("LengthIndicator")}
            {data.estimatedTime &&
              ` · ${data.estimatedTime} ${t("TimeIndicator")}`}
          </span>
        </div>
        <div className="control flex flex-col items-end justify-between align-bottom">
          {clear && (
            <Button onClick={clear} size="xs" className="rounded-full">
              <X className="h-5 w-5" />
              <span className="sr-only">{t("Clear")}</span>
            </Button>
          )}
          <Link
            href={routes.path.route({ path: data.slug })}
            className="mt-auto inline-flex h-8 w-8 items-center justify-center whitespace-nowrap rounded-full border border-primary bg-primary-foreground text-sm font-medium text-primary ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-inactive disabled:text-inactive-foreground"
          >
            <ArrowRight className="h-4 w-4" />
            <span className="sr-only">{t("GoToPathPage")}</span>
          </Link>
        </div>
      </div>

      <div className="relative aspect-video w-full overflow-hidden rounded-xl">
        <Image
          src={data?.image?.url || "/assets/placeholder-image.png"}
          placeholder={undefined}
          fill={true}
          role={data.image?.alternativeText ? "img" : "presentation"}
          className="h-full w-full object-cover object-center"
          alt={data.image?.alternativeText || ""}
        />
      </div>
      <div className="content flex flex-col gap-5 text-sm">
        {data.terrain && (
          <p>
            {t("TerrainLabel")}: {data.terrain}
          </p>
        )}
        {data.extraInfo && (
          <p>
            {t("ExtraInfoLabel")}: {data.extraInfo}
          </p>
        )}
      </div>
    </div>
  );
}
